import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import gamlensLogo from '@/assets/images/logo.png';

const sections = [
  {
    title: 'Introduction',
    content: 'GameLens is a sports technology platform using cameras, computer vision, artificial intelligence, software, and mobile applications for sports-related services. This Privacy Policy describes how we collect, use, disclose, and otherwise process personal information in connection with our services.'
  },
  {
    title: 'Information We Collect',
    subsections: [
      {
        title: 'Account Information',
        content: 'When you create an account, we collect information such as your name, email address, phone number, and other profile information you provide.'
      },
      {
        title: 'Sports & Match Information',
        content: 'We collect information about sports events, matches, teams, players, and performance data related to your use of our services.'
      },
      {
        title: 'Camera and Video Data',
        content: 'Our systems capture video and images from cameras installed at venues for sports analysis and officiating purposes.'
      },
      {
        title: 'Device Information',
        content: 'We collect information about your device, including device type, operating system, unique device identifiers, and mobile network information.'
      },
      {
        title: 'Usage Information',
        content: 'We collect information about how you interact with our services, including features accessed, content viewed, and actions taken.'
      },
      {
        title: 'Location Information',
        content: 'With your permission, we may collect precise location information from your mobile device.'
      }
    ]
  },
  {
    title: 'How We Use Information',
    content: 'We use collected information to: provide and improve our services, perform sports analysis and officiating, send you promotional communications, ensure security and prevent fraud, and comply with legal obligations.'
  },
  {
    title: 'Artificial Intelligence and Sports Analysis',
    content: 'GameLens uses artificial intelligence and machine learning models to analyze sports video footage, detect events, track players and objects, and make officiating decisions. Our AI systems process video data in real-time to provide accurate, consistent decision-making.'
  },
  {
    title: 'Video Footage',
    content: 'Video footage captured by our systems is processed, analyzed, and stored. Footage may be retained for quality assurance, training, and analysis purposes. Users who appear in recorded footage consent to its capture and use for sports-related services.'
  },
  {
    title: 'Sharing of Information',
    content: 'We do not sell your personal information. We may share information with service providers, business partners, and as required by law. Third parties are contractually obligated to maintain confidentiality and security.'
  },
  {
    title: 'Data Security',
    content: 'We implement appropriate technical and organizational measures to protect personal information against unauthorized access, alteration, disclosure, or destruction.'
  },
  {
    title: 'Data Retention',
    content: 'We retain personal information for as long as necessary to provide our services and fulfill the purposes outlined in this policy, unless a longer retention period is required by law.'
  },
  {
    title: 'Account Deletion and Data Deletion',
    content: 'You have the right to request deletion of your account and associated personal information. To request account and data deletion, please contact us at meetrathod@gamelens.live. We will process your request within 30 days. Please note that certain information may need to be retained for legal, security, fraud-prevention, or other permitted purposes.'
  },
  {
    title: 'Privacy Rights',
    content: 'Depending on your jurisdiction, you may have rights including access to your personal information, correction of inaccurate data, and deletion of your information, subject to certain conditions and exceptions.'
  },
  {
    title: 'Children\'s Privacy',
    content: 'Our services are not intended for individuals under 13 years of age. We do not knowingly collect information from children under 13. If we become aware that we have collected information from a child under 13, we will take steps to delete such information.'
  },
  {
    title: 'Third-Party Services and SDKs',
    content: 'Our services may integrate third-party services, software development kits (SDKs), and analytics tools. These third parties may collect information about your use of our services in accordance with their own privacy policies.'
  },
  {
    title: 'International Data Processing',
    content: 'Your information may be transferred to, stored in, and processed in countries other than your country of residence. These countries may have data protection laws that differ from your home country.'
  },
  {
    title: 'Cookies and Similar Technologies',
    content: 'We use cookies and similar tracking technologies to enhance user experience, remember preferences, and analyze usage patterns. You can control cookie settings through your browser.'
  },
  {
    title: 'Changes to Privacy Policy',
    content: 'We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on our website or through other appropriate means.'
  },
  {
    title: 'Contact Us',
    content: 'If you have questions about this Privacy Policy or our privacy practices, please contact us at: meetrathod@gamelens.live'
  },
  {
    title: 'Governing Law',
    content: 'This Privacy Policy is governed by and construed in accordance with applicable laws and regulations.'
  }
];

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gl-black font-body">
      {/* Header */}
      <div className="bg-gl-surface border-b border-white/6 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 text-white" />
          </button>
          <div className="flex items-center gap-2">
            <img src={gamlensLogo} alt="GameLens" className="h-7 w-auto object-contain" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="mb-12">
          <h1 className="font-heading font-bold text-4xl md:text-5xl text-white tracking-tight mb-4">Privacy Policy</h1>
          <p className="text-base font-body text-white/60">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        <div className="space-y-8">
          {sections.map((section, idx) => (
            <div key={idx} className="bg-gl-surface border border-white/6 rounded-2xl p-8">
              <h2 className="font-heading font-bold text-2xl text-white mb-4">{section.title}</h2>
              
              {section.subsections ? (
                <div className="space-y-6">
                  {section.subsections.map((subsection, sidx) => (
                    <div key={sidx}>
                      <h3 className="font-heading font-semibold text-lg text-white/90 mb-2">{subsection.title}</h3>
                      <p className="text-base font-body text-white/60 leading-relaxed">{subsection.content}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-base font-body text-white/60 leading-relaxed">{section.content}</p>
              )}
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-12 bg-gradient-to-r from-gl-orange/5 to-gl-blue/5 border border-white/8 rounded-2xl p-8 text-center">
          <h3 className="font-heading font-bold text-xl text-white mb-2">Have Questions?</h3>
          <p className="text-base font-body text-white/60 mb-4">Contact us at meetrathod@gamelens.live for privacy-related inquiries.</p>
          <a 
            href="mailto:meetrathod@gamelens.live"
            className="inline-block px-6 py-2 rounded-xl bg-gradient-brand text-white text-sm font-body font-semibold hover:opacity-90 transition-opacity"
          >
            Send Email
          </a>
        </div>
      </div>
    </div>
  );
}
